import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuthSession } from '../auth';
import { authFetch, isDelegated } from '../auth';
import { AnalysisRetentionPanel } from './AnalysisRetentionPanel';
import type { RetentionScope } from './analysisRetention';
import { ProcessingConsentPanel } from './ProcessingConsentPanel';
import type { Document } from './dueDiligence';
import { analysisJobLabel, analysisJobMessage, analysisCanStop, analysisCanStart, analysisPollDelay, type ManagedJobStatus } from './analysisJobStatus';
import type { Planning } from './purchasePlanning';
import { eur } from './types';
import { fileSize } from './RecordingUploader';
import { acquisitionFeeTemplates } from './purchasePlanning';
import {
  analysisConsentBinding,
  analysisServiceLabel,
  analysisUnavailableMessage,
  type AnalysisCapabilities,
} from './analysisServices';
export type { AnalysisCapabilities } from './analysisServices';
import {
  recordingSegmentLabel,
  recordingSeekSeconds,
  type TranscriptSegment,
} from './recordingTimeline';
import {
  recordingEvidenceLocator,
  recordingEvidenceMessage,
  type RecordingEvidence,
} from './recordingEvidence';

export type AnalysisSuggestion = {
  kind: 'fact' | 'task' | 'cost' | 'finance' | 'unit';
  field: string;
  title: string;
  claim: string;
  locator: string;
  quote: string;
  scope: string;
  numericValue: number | null;
  lowEur: number | null;
  highEur: number | null;
};
type Job = {
  id: string;
  documentId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  model: string;
  providerOrigin: string;
  managedStatus?: ManagedJobStatus | null;
  privateOutputRemovedAt?:string|null;
  transcript?: {
    text?: string;
    segments?: TranscriptSegment[];
  };
  result?: { summary: string; suggestions: AnalysisSuggestion[] };
  sourceEvidence?: RecordingEvidence[] | null;
  accepted: number[];
  acceptedSources?: Record<number, string>;
  error?: string;
};
function RecordingPlayer({
  user,
  projectId,
  document,
  segments,
}: {
  user: AuthSession;
  projectId: string;
  document: Document;
  segments: NonNullable<NonNullable<Job['transcript']>['segments']>;
}) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [duration, setDuration] = useState<number | null>(null);
  const media = useRef<HTMLMediaElement | null>(null);
  const currentUrl = useRef('');
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (currentUrl.current) URL.revokeObjectURL(currentUrl.current);
    };
  }, []);
  const load = async () => {
    setLoading(true);
    try {
      const blob = await authFetch(
        user,
        `/api/research/properties/${encodeURIComponent(projectId)}/due-diligence/documents/${encodeURIComponent(document.id)}`,
        {},
        'blob',
      );
      if (mounted.current) {
        const next = URL.createObjectURL(
          new Blob([blob], { type: document.mediaType }),
        );
        currentUrl.current = next;
        setUrl(next);
      }
    } catch {
      if (mounted.current) setError('Aufnahme konnte nicht geladen werden.');
    } finally {
      if (mounted.current) setLoading(false);
    }
  };
  return (
    <div className="journey-recording">
      {!url ? (
        <button
          disabled={loading}
          className="quiet-button"
          onClick={() => void load()}
        >
          {loading
            ? 'Lädt Aufnahme…'
            : 'Originalaufnahme zum Abgleichen öffnen'}
        </button>
      ) : document.mediaType.startsWith('video/') ? (
        <video
          ref={media as React.RefObject<HTMLVideoElement>}
          src={url}
          controls
          preload="metadata"
          onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
        />
      ) : (
        <audio
          ref={media as React.RefObject<HTMLAudioElement>}
          src={url}
          controls
          preload="metadata"
          onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
        />
      )}
      {error && <p role="alert">{error}</p>}
      {segments.some((segment) => segment.approximate) && (
        <p className="journey-caption">
          Zeitmarken mit „Abschnitt“ öffnen den Beginn eines Aufnahmeabschnitts.
          Die genaue Stelle der Aussage ist noch offen.
        </p>
      )}
      {segments.map((segment, index) => (
        <article key={index}>
          <button
            className="journey-time"
            disabled={!url || recordingSeekSeconds(segment.start, duration) == null}
            onClick={() => {
              const seconds = recordingSeekSeconds(segment.start, duration);
              if (media.current && seconds != null) {
                media.current.currentTime = seconds;
                media.current.focus();
              }
            }}
          >
            {recordingSegmentLabel(segment)}
          </button>
          <p>{segment.text}</p>
        </article>
      ))}
    </div>
  );
}
export function AnalysisPanel({
  user,
  projectId,
  documents,
  planning,
  version,
  dirty,
  capabilities,
  onAccepted,
}: {
  user: AuthSession | null;
  projectId: string;
  documents: Document[];
  planning: Planning;
  version: number;
  dirty: boolean;
  capabilities: AnalysisCapabilities | null;
  onAccepted: () => Promise<void>;
}) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const jobsGeneration=useRef({value:0});
  const removedOutputs=useRef(new Map<string,string>());
  const applyJobs=useCallback((items:Job[])=>setJobs(items.map(job=>removedOutputs.current.has(job.id)?{...job,transcript:undefined,result:undefined,error:undefined,privateOutputRemovedAt:removedOutputs.current.get(job.id)}:job)),[]);
  const [consents, setConsents] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [recordingAnchors, setRecordingAnchors] = useState<Record<string, string>>({});
  const [costCategories, setCostCategories] = useState<Record<string, string>>(
    {},
  );
  const [feeKinds, setFeeKinds] = useState<Record<string, string>>({});
  const base = `/api/research/properties/${encodeURIComponent(projectId)}/analysis-jobs`;
  useEffect(() => {
    if (!user) return;
    const counter=jobsGeneration.current;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    let delay = 15000;
    const load = async () => {
      const ticket=++jobsGeneration.current.value;
      try {
        const result = await authFetch(user, base);
        if (active&&ticket===jobsGeneration.current.value) {
          applyJobs(result.items);
          delay = analysisPollDelay(result.items);
        }
      } catch (reason) {
        if (active)
          setError(
            reason instanceof Error
              ? reason.message
              : 'Analysen konnten nicht geladen werden.',
          );
        delay = Math.min(60000, delay * 2);
      } finally {
        if (active) timer = setTimeout(() => void load(), delay);
      }
    };
    void load();
    return () => {
      active = false;
      counter.value++;
      clearTimeout(timer);
    };
  }, [base, user,applyJobs]);
  const reloadAfterRetention=async(scope:RetentionScope,removed:boolean)=>{
    const ticket=++jobsGeneration.current.value;
    const result=await authFetch(user!,base+'?'+new URLSearchParams({actorId:scope.actorId,householdId:scope.householdId}),{cache:'no-store',redirect:'error'});
    if(ticket!==jobsGeneration.current.value||result.actorId!==scope.actorId||result.householdId!==scope.householdId||result.listingId!==scope.listingId)throw new Error('Anmeldung oder Haushalt hat gewechselt. Ursprüngliches Ergebnis erneut prüfen.');
    const target=(result.items as Job[]).find(job=>job.id===scope.jobId);
    if(removed&&target&&!target.privateOutputRemovedAt)throw new Error('Entfernen noch nicht im gespeicherten Stand bestätigt.');
    if(target?.privateOutputRemovedAt)removedOutputs.current.set(scope.jobId,target.privateOutputRemovedAt);
    applyJobs(result.items);
  };
  const stop = async (job: Job) => {
    if (!user) return;
    setBusy(job.id);
    try {
      await authFetch(user, `${base}/${encodeURIComponent(job.id)}/cancel`, {
        method: 'POST',
      });
      const result = await authFetch(user, base);
      applyJobs(result.items);
    } catch {
      setError('Auftrag konnte nicht gestoppt werden.');
    } finally {
      setBusy('');
    }
  };
  const start = async (document: Document) => {
    if (
      !user ||
      !capabilities?.enabled || capabilities.connection?.mode === 'managed' ||
      !analysisCanStart(document.id, jobs) ||
      consents[document.id] !==
        analysisConsentBinding(
          capabilities,
          /^(audio|video)\//.test(document.mediaType),
        )
    )
      return;
    setBusy(document.id);
    setError('');
    try {
      await authFetch(user, base, {
        method: 'POST',
        body: JSON.stringify({
          documentId: document.id,
          documentSha256: document.sha256,
          providerOrigin: capabilities.providerOrigin,
          transcriptionProviderOrigin:
            capabilities.transcriptionProviderOrigin ??
            capabilities.providerOrigin,
          consent: true,
          documentModel: capabilities.documentModel,
          transcriptionModel: capabilities.transcriptionModel,
        }),
      });
      const result = await authFetch(user, base);
      applyJobs(result.items);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : 'Analyse konnte nicht gestartet werden.',
      );
    } finally {
      setBusy('');
    }
  };
  const accept = async (
    job: Job,
    index: number,
    suggestion: AnalysisSuggestion,
  ) => {
    if (!user || dirty) return;
    const recording = Boolean(job.transcript) || documents.some((document) =>
      document.id === job.documentId && /^(audio|video)\//.test(document.mediaType));
    if (recording && !recordingEvidenceLocator(job.sourceEvidence?.[index], recordingAnchors[`${job.id}:${index}`])) return;
    setBusy(`${job.id}:${index}`);
    setError('');
    try {
      const selected = targets[`${job.id}:${index}`];
      await authFetch(user, `${base}/${encodeURIComponent(job.id)}/accept`, {
        method: 'POST',
        body: JSON.stringify({
          expectedVersion: version,
          indices: [index],
          costTargets:
            suggestion.kind === 'cost' && selected ? { [index]: selected } : {},
          unitTargets:
            suggestion.kind === 'unit' && selected ? { [index]: selected } : {},
          financeTargets:
            suggestion.kind === 'finance' && selected
              ? { [index]: selected }
              : {},
          costCategories: costCategories[`${job.id}:${index}`]
            ? { [index]: costCategories[`${job.id}:${index}`] }
            : {},
          feeKinds: feeKinds[`${job.id}:${index}`]
            ? { [index]: feeKinds[`${job.id}:${index}`] }
            : {},
          recordingAnchors: recording && /^\d+$/.test(recordingAnchors[`${job.id}:${index}`] ?? '')
            ? { [index]: Number(recordingAnchors[`${job.id}:${index}`]) } : {},
        }),
      });
      await onAccepted();
      const result = await authFetch(user, base);
      applyJobs(result.items);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Übernahme fehlgeschlagen.',
      );
    } finally {
      setBusy('');
    }
  };
  return (
    <div className="journey-analysis">
      <div className="journey-analysis-intro">
        <div>
          <span className="journey-ai-mark">✧</span>
          <h3>Aus Unterlagen werden nächste Schritte</h3>
        </div>
        <p>
          Die Analyse liest Dokumente, transkribiert Besuche und schlägt belegte
          Angaben vor. Du siehst die Quelle und entscheidest, was in den Plan
          eingeht.
        </p>
      </div>
      {!user ? (
        <p className="journey-caption">
          Die Demo versendet keine Dateien an KI-Dienste. Im verbundenen
          Haushalt erscheinen hier Aufträge, Transkripte und überprüfbare
          Vorschläge.
        </p>
      ) : !capabilities?.enabled ? (
        <p className="journey-caption">
          {analysisUnavailableMessage(capabilities)}
        </p>
      ) : (
        <p className="journey-caption">
          Dokumentanalyse durch {capabilities.providerOrigin}; Transkription
          durch{' '}
          {capabilities.transcriptionProviderOrigin ??
            capabilities.providerOrigin}
          . Dokumente bis {capabilities.documentAnalysisMaxMb} MB, Aufnahmen bis{' '}
          {fileSize(capabilities.recordingMaxMb * 1024 * 1024)} / zwei Stunden.
          Automatische Transkripte und Sprecherzuordnung können Fehler
          enthalten.
        </p>
      )}
      {error && (
        <p className="journey-alert" role="alert">
          {error}
        </p>
      )}
      {user && capabilities?.connection?.mode === 'managed' && <ProcessingConsentPanel key={`${isDelegated(user)?'delegated':user.profile.sub}:${projectId}`} user={user} projectId={projectId} documents={documents} dirty={dirty}/> }
      {user &&
        capabilities?.connection?.mode !== 'managed' && capabilities?.enabled &&
        documents.map((document) => {
          const job = jobs.find((job) => job.documentId === document.id);
          return (
            <div className="journey-analysis-source" key={document.id}>
              <div>
                <strong>{document.name}</strong>
                <span>
                  {job ? analysisJobLabel(job) : 'Noch nicht analysiert'}
                </span>
              </div>
              {(!job || job.status === 'failed') && analysisCanStart(document.id, jobs) && (
                <>
                  <label className="journey-checkbox">
                    <input
                      type="checkbox"
                      checked={
                        consents[document.id] ===
                        analysisConsentBinding(
                          capabilities,
                          /^(audio|video)\//.test(document.mediaType),
                        )
                      }
                      onChange={(event) =>
                        setConsents({
                          ...consents,
                          [document.id]: event.target.checked
                            ? analysisConsentBinding(
                                capabilities,
                                /^(audio|video)\//.test(document.mediaType),
                              )
                            : '',
                        })
                      }
                    />
                    Diese Datei und das daraus erzeugte Transkript dürfen durch{' '}
                    {analysisServiceLabel(
                      capabilities,
                      /^(audio|video)\//.test(document.mediaType),
                    )}{' '}
                    verarbeitet werden. Bei Aufnahmen sind die nötigen
                    Einwilligungen geklärt.
                  </label>
                  <button
                    disabled={
                      consents[document.id] !==
                        analysisConsentBinding(
                          capabilities,
                          /^(audio|video)\//.test(document.mediaType),
                        ) || Boolean(busy)
                    }
                    onClick={() => void start(document)}
                  >
                    {busy === document.id
                      ? 'Startet…'
                      : job?.transcript
                        ? 'Auswertung erneut versuchen'
                        : 'Analysieren & transkribieren'}
                  </button>
                </>
              )}
            </div>
          );
        })}
      {jobs.map((job) => (
        <section className="journey-analysis-result" key={job.id}>
          <div className="journey-section-heading">
            <div>
              <h3>
                {documents.find((d) => d.id === job.documentId)?.name ??
                  'Quelle'}
              </h3>
              <small>
                {job.model} ·{' '}
                {analysisJobLabel(job)}
              </small>
            </div>
            {analysisCanStop(job) && (
              <button
                disabled={Boolean(busy)}
                className="quiet-button"
                onClick={() => void stop(job)}
              >
                Auftrag stoppen
              </button>
            )}
          </div>
          {analysisJobMessage(job) && <p className="journey-caption" role="status">{analysisJobMessage(job)}</p>}
          {!job.managedStatus && job.error && <p className="journey-alert">{job.error}</p>}
          {user&&!isDelegated(user)&&<AnalysisRetentionPanel key={`${user.profile.sub}:${job.id}`} user={user} listingId={projectId} jobId={job.id} removedAt={job.privateOutputRemovedAt} blocked={dirty||Boolean(busy)} onResolved={reloadAfterRetention}/>}
          {job.result && <p>{job.result.summary}</p>}
          {job.transcript && (
            <details className="journey-transcript">
              <summary>Besuchstranskript mit Fundstellen</summary>
              <p className="journey-caption">
                Maschinelles Transkript, ungeprüft. Sprecherkennungen sind keine
                bestätigten Personen oder Fachrollen.
              </p>
              {user &&
              documents.find((d) => d.id === job.documentId) &&
              job.transcript.segments?.length ? (
                <RecordingPlayer
                  key={job.documentId}
                  user={user}
                  projectId={projectId}
                  document={documents.find((d) => d.id === job.documentId)!}
                  segments={job.transcript.segments}
                />
              ) : (
                <pre>{job.transcript.text}</pre>
              )}
            </details>
          )}
          {dirty && job.result && (
            <p className="journey-caption">
              Speichere deinen aktuellen Entwurf, bevor neue Daten übernommen
              werden. So bleiben parallele Änderungen geschützt.
            </p>
          )}
          {job.result?.suggestions.map((suggestion, index) => {
            const key = `${job.id}:${index}`;
            const accepted = job.accepted.includes(index);
            const recording = Boolean(job.transcript) || documents.some((document) =>
              document.id === job.documentId && /^(audio|video)\//.test(document.mediaType));
            const sourceEvidence = job.sourceEvidence?.[index];
            const sourceLocator = recording
              ? accepted ? job.acceptedSources?.[index] ?? null
                : recordingEvidenceLocator(sourceEvidence, recordingAnchors[key]) : suggestion.locator;
            const loanField =
              suggestion.kind === 'finance' &&
              ['loanAmount', 'interestRate', 'repaymentRate'].includes(
                suggestion.field,
              );
            const candidates =
              suggestion.kind === 'cost'
                ? planning.costs
                : suggestion.kind === 'unit'
                  ? planning.units.filter(
                      (unit) =>
                        unit.use ===
                        (suggestion.field === 'privateAreaM2'
                          ? 'private'
                          : 'rental'),
                    )
                  : loanField
                    ? planning.requests.map((request) => ({
                        id: request.id,
                        title: request.lender,
                      }))
                    : [];
            const needsTarget =
              (suggestion.kind === 'cost' && candidates.length > 0) ||
              (suggestion.kind === 'unit' && candidates.length > 1) ||
              (loanField && candidates.length > 0);
            const creatingCost =
              suggestion.kind === 'cost' &&
              (!candidates.length || targets[key] === '__new__');
            const needsCategory =
              creatingCost &&
              (!costCategories[key] ||
                (costCategories[key] === 'fees' && !feeKinds[key]));
            return (
              <article
                className={`journey-suggestion ${accepted ? 'accepted' : ''}`}
                key={index}
              >
                <div>
                  <span className="journey-stage">
                    {accepted
                      ? 'Übernommen'
                      : suggestion.kind === 'cost'
                        ? 'Kostenstand'
                        : suggestion.kind === 'task'
                          ? 'Nächste Frage'
                          : suggestion.kind === 'finance'
                            ? 'Finanzdaten'
                            : suggestion.kind === 'unit'
                              ? 'Gebäudeteil'
                              : 'Befund'}
                  </span>
                  <h4>{suggestion.title}</h4>
                </div>
                <p>{suggestion.claim}</p>
                {suggestion.kind === 'cost' && (
                  <strong>
                    {eur(suggestion.lowEur)} – {eur(suggestion.highEur)}
                  </strong>
                )}
                {suggestion.numericValue !== null && (
                  <strong>
                    {suggestion.numericValue}{' '}
                    {suggestion.field.includes('Area')
                      ? 'm²'
                      : suggestion.field.includes('Rate')
                        ? '%'
                        : suggestion.field === 'completionYear'
                          ? ''
                          : '€'}
                  </strong>
                )}
                <blockquote>
                  <p>{suggestion.quote}</p>
                  <cite>
                    {sourceLocator ?? 'Fundstelle offen'} ·{' '}
                    {documents.find((d) => d.id === job.documentId)?.name ??
                      'Originalquelle'}
                  </cite>
                </blockquote>
                {recording && (
                  <p className="journey-caption">{accepted
                    ? job.acceptedSources?.[index]
                      ? 'Mit dieser Fundstelle übernommen. Transkript und Sprecherzuordnung bleiben ungeprüft.'
                      : 'Bei dieser älteren Übernahme wurde die Fundstelle nicht separat gespeichert. Gleiche sie mit der Originalaufnahme ab.'
                    : recordingEvidenceMessage(sourceEvidence)}</p>
                )}
                {!accepted && recording && sourceEvidence?.state === 'ambiguous' && (
                  <label>
                    Welche Fundstelle gehört zu diesem Vorschlag?
                    <select
                      aria-label="Fundstelle für Analysevorschlag"
                      value={recordingAnchors[key] ?? ''}
                      onChange={(event) => setRecordingAnchors({ ...recordingAnchors, [key]: event.target.value })}
                    >
                      <option value="">Bitte an der Aufnahme abgleichen</option>
                      {sourceEvidence.anchors.map((anchor, index) => (
                        <option value={index} key={index}>{anchor.locator}</option>
                      ))}
                    </select>
                    {sourceEvidence.moreLocations && <small>Weitere Fundstellen vorhanden. Übernimm den Vorschlag nur, wenn eine dieser Stellen die Aussage belegt.</small>}
                  </label>
                )}
                {!accepted && needsTarget && (
                  <label>
                    {suggestion.kind === 'cost'
                      ? 'Welchen Kostenstand verbessert dieser Befund?'
                      : loanField
                        ? 'Zu welcher Anfrage oder welchem Darlehensbaustein gehört die Angabe?'
                        : 'Zu welchem Gebäudeteil gehört die Angabe?'}
                    <select
                      aria-label="Ziel für Analysevorschlag"
                      value={targets[key] ?? ''}
                      onChange={(event) =>
                        setTargets({ ...targets, [key]: event.target.value })
                      }
                    >
                      <option value="">Bitte zuordnen</option>
                      {candidates.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.title}
                        </option>
                      ))}
                      <option value="__new__">Als neue Position anlegen</option>
                    </select>
                  </label>
                )}
                {!accepted && creatingCost && (
                  <label>
                    Kostenart für diesen neuen Vorschlag
                    <select
                      aria-label="Kostenart für Analysevorschlag"
                      value={costCategories[key] ?? ''}
                      onChange={(event) =>
                        setCostCategories({
                          ...costCategories,
                          [key]: event.target.value,
                        })
                      }
                    >
                      <option value="">Bitte einordnen</option>
                      <option value="repair">Arbeit / Sanierung</option>
                      <option value="inspection">Fachprüfung / Beratung</option>
                      <option value="fees">
                        Erwerbs- / Finanzierungskosten
                      </option>
                    </select>
                  </label>
                )}
                {!accepted &&
                  creatingCost &&
                  costCategories[key] === 'fees' && (
                    <label>
                      Erwerbskostenart
                      <select
                        aria-label="Erwerbskostenart für Analysevorschlag"
                        value={feeKinds[key] ?? ''}
                        onChange={(event) =>
                          setFeeKinds({
                            ...feeKinds,
                            [key]: event.target.value,
                          })
                        }
                      >
                        <option value="">Bitte zuordnen</option>
                        {acquisitionFeeTemplates
                          .filter(
                            (template) =>
                              template.id === 'other' ||
                              !planning.costs.some(
                                (cost) =>
                                  cost.category === 'fees' &&
                                  cost.feeKind === template.id,
                              ),
                          )
                          .map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.title}
                            </option>
                          ))}
                      </select>
                    </label>
                  )}
                {!accepted && (
                  <button
                    disabled={
                      dirty ||
                      Boolean(busy) ||
                      (recording && !sourceLocator) ||
                      (needsTarget && !targets[key]) ||
                      needsCategory
                    }
                    onClick={() => void accept(job, index, suggestion)}
                  >
                    {busy === key
                      ? 'Übernimmt…'
                      : 'Geprüft in meinen Plan übernehmen'}
                  </button>
                )}
                {accepted && (
                  <small>
                    Mit Beleg und Versionsverlauf übernommen. Betroffene
                    Fachprüfungen bleiben offen.
                  </small>
                )}
              </article>
            );
          })}
        </section>
      ))}
    </div>
  );
}
